import React from 'react';
import { Sparkles, Wand2, FileText, Hash, Image, Clock, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

const features = [
    {
        icon: Wand2,
        title: 'Content Generation',
        description: 'Generate engaging post content based on your topic or keywords'
    },
    {
        icon: FileText,
        title: 'Caption Writing',
        description: 'Create compelling captions for your images and videos'
    },
    {
        icon: Hash,
        title: 'Hashtag Suggestions',
        description: 'Get relevant hashtag recommendations to increase reach'
    },
    {
        icon: Image,
        title: 'Image Ideas',
        description: 'Receive creative image and visual content suggestions'
    },
    {
        icon: Clock,
        title: 'Best Time Analysis',
        description: 'Learn the optimal times to post for maximum engagement'
    },
    {
        icon: Zap,
        title: 'A/B Testing Ideas',
        description: 'Get variations of your content to test what works best'
    }
];

const AIContentGenerator = () => {
    return (
        <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="text-center max-w-2xl mx-auto mb-12">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/25 mb-6"
                >
                    <Sparkles className="w-10 h-10 text-white" />
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-3xl font-bold text-slate-900 dark:text-white mb-4"
                >
                    AI Content Assistant
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-lg text-slate-600 dark:text-slate-400"
                >
                    Supercharge your social media content creation with AI-powered tools.
                    This feature is coming soon to help you create better content, faster.
                </motion.p>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {features.map((feature, index) => (
                    <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * (index + 3) }}
                        className="p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                    >
                        <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30 w-fit mb-4">
                            <feature.icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                            {feature.title}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-400">
                            {feature.description}
                        </p>
                    </motion.div>
                ))}
            </div>

            {/* Coming soon badge */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="text-center mt-12"
            >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-sm font-medium">
                    <Sparkles className="w-4 h-4" />
                    Coming Soon
                </div>
                <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                    We're working hard to bring you these AI-powered features.
                    <br />
                    Stay tuned for updates!
                </p>
            </motion.div>

            {/* Waitlist/Notify section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="mt-12 max-w-md mx-auto"
            >
                <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 text-center">
                        Get Early Access
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-4">
                        Be the first to know when AI Content Assistant launches.
                    </p>
                    <div className="flex gap-2">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            disabled
                            className="flex-1 px-4 py-2.5 rounded-xl border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                        />
                        <button
                            disabled
                            className="px-5 py-2.5 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Notify Me
                        </button>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-3">
                        Notification signup coming soon
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default AIContentGenerator;
